import React, { useState, useEffect, useCallback } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Activity,
  Zap,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Settings,
  Gauge,
  Clock,
} from 'lucide-react';
import useSimulationStore from '../../store/simulationStore';
import { formatQps, healthScoreColor, utilizationColor } from '../../validation/traffic-types';

const SimulationPanel = ({ designJson, componentList, onClose }) => {
  const {
    isSimulating,
    simulationState,
    error,
    config,
    setConfig,
    runSimulation,
    isAnimating,
    animationSpeed,
    currentStep,
    startAnimation,
    stopAnimation,
    setAnimationSpeed,
    nextStep,
    prevStep,
    resetAnimation,
    clearResults,
  } = useSimulationStore();

  const [showSettings, setShowSettings] = useState(false);
  const [localConfig, setLocalConfig] = useState({
    totalQps: 10000,
    readWriteRatio: 0.8,
    trafficPattern: 'steady',
  });

  // Auto-advance animation
  useEffect(() => {
    if (!isAnimating || !simulationState?.steps) return;

    const interval = setInterval(() => {
      nextStep();
    }, 1000 / animationSpeed);

    return () => clearInterval(interval);
  }, [isAnimating, animationSpeed, nextStep, simulationState]);

  // Stop animation at end
  useEffect(() => {
    if (simulationState?.steps && currentStep >= simulationState.steps.length - 1) {
      stopAnimation();
    }
  }, [currentStep, simulationState, stopAnimation]);

  const handleRunSimulation = useCallback(async () => {
    // Set entry traffic based on total QPS
    const entryTraffic = {};
    // We'll let the backend figure out entry points
    setConfig({
      readWriteRatio: localConfig.readWriteRatio,
      trafficPattern: localConfig.trafficPattern,
    });

    try {
      await runSimulation(designJson, componentList);
    } catch (err) {
      console.error('Simulation failed:', err);
    }
  }, [designJson, componentList, localConfig, setConfig, runSimulation]);

  const handleReset = () => {
    clearResults();
    resetAnimation();
  };

  const currentStepData = simulationState?.steps?.[currentStep];
  const totalSteps = simulationState?.steps?.length || 0;

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" />
          <h3 className="font-semibold text-white">Traffic Simulation</h3>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`p-2 rounded-lg transition-colors ${
            showSettings ? 'bg-slate-700 text-cyan-400' : 'hover:bg-slate-700 text-slate-400'
          }`}
        >
          <Settings className="w-4 h-4" />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="p-4 border-b border-slate-700 bg-slate-850 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Total QPS</label>
            <input
              type="range"
              min="1000"
              max="100000"
              step="1000"
              value={localConfig.totalQps}
              onChange={(e) => setLocalConfig({ ...localConfig, totalQps: parseInt(e.target.value) })}
              className="w-full accent-cyan-500"
            />
            <div className="text-sm text-slate-300 mt-1">{formatQps(localConfig.totalQps)} QPS</div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Read/Write Ratio: {Math.round(localConfig.readWriteRatio * 100)}% reads
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={localConfig.readWriteRatio}
              onChange={(e) => setLocalConfig({ ...localConfig, readWriteRatio: parseFloat(e.target.value) })}
              className="w-full accent-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-1">Traffic Pattern</label>
            <select
              value={localConfig.trafficPattern}
              onChange={(e) => setLocalConfig({ ...localConfig, trafficPattern: e.target.value })}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white"
            >
              <option value="steady">Steady</option>
              <option value="spike">Spike (2x)</option>
              <option value="gradual">Gradual Increase</option>
            </select>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex gap-2">
          <button
            onClick={handleRunSimulation}
            disabled={isSimulating}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-600 text-white rounded-lg transition-colors"
          >
            {isSimulating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Simulating...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Run Simulation
              </>
            )}
          </button>
          <button
            onClick={handleReset}
            disabled={!simulationState}
            className="p-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="mt-2 p-2 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {simulationState && (
        <>
          {/* Health Overview */}
          <div className="p-4 border-b border-slate-700">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Gauge className="w-4 h-4" />
                  Health Score
                </div>
                <div
                  className="text-2xl font-bold"
                  style={{ color: healthScoreColor(simulationState.health_score || 0) }}
                >
                  {Math.round(simulationState.health_score || 0)}
                </div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  P99 Latency
                </div>
                <div className="text-2xl font-bold text-white">
                  {simulationState.p99_latency_ms?.toFixed(1) || 0}ms
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <div className="text-slate-400">Processed</div>
                <div className="text-green-400 font-medium">
                  {formatQps(simulationState.total_qps_processed || 0)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-400">Dropped</div>
                <div className="text-red-400 font-medium">
                  {(simulationState.dropped_traffic_pct || 0).toFixed(1)}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-slate-400">Bottlenecks</div>
                <div className="text-yellow-400 font-medium">
                  {simulationState.bottlenecks?.length || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Animation Controls */}
          {simulationState.steps?.length > 0 && (
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="p-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={isAnimating ? stopAnimation : startAnimation}
                  className="p-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg"
                >
                  {isAnimating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button
                  onClick={nextStep}
                  disabled={currentStep >= totalSteps - 1}
                  className="p-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                <div className="flex-1 mx-2">
                  <input
                    type="range"
                    min="0"
                    max={totalSteps - 1}
                    value={currentStep}
                    onChange={(e) => useSimulationStore.getState().setCurrentStep(parseInt(e.target.value))}
                    className="w-full accent-cyan-500"
                  />
                </div>

                <select
                  value={animationSpeed}
                  onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
                  className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm"
                >
                  <option value="0.5">0.5x</option>
                  <option value="1">1x</option>
                  <option value="2">2x</option>
                  <option value="4">4x</option>
                </select>
              </div>
              <div className="text-center text-sm text-slate-400 mt-2">
                Step {currentStep + 1} of {totalSteps}
              </div>
            </div>
          )}

          {/* Bottlenecks */}
          {simulationState.bottlenecks?.length > 0 && (
            <div className="p-4">
              <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Bottlenecks Detected
              </h4>
              <div className="space-y-2">
                {simulationState.bottlenecks.map((bottleneck, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-700/50 rounded-lg p-3 border-l-2"
                    style={{ borderColor: utilizationColor(bottleneck.utilization) }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-white">{bottleneck.node_label}</span>
                      <span
                        className="text-sm px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: `${utilizationColor(bottleneck.utilization)}20`,
                          color: utilizationColor(bottleneck.utilization),
                        }}
                      >
                        {Math.round((bottleneck.utilization || 0) * 100)}%
                      </span>
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      {formatQps(bottleneck.current_qps)} / {formatQps(bottleneck.max_qps)} QPS
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{bottleneck.recommendation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SimulationPanel;
